// Stripe subscription statuses this codebase branches on. Values match the
// Stripe API verbatim — see https://docs.stripe.com/api/subscriptions/object#subscription_object-status
export enum StripeSubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  UNPAID = 'unpaid',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused',
}

export enum BillingUiStateKind {
  ACTIVE = 'ACTIVE',
  TRIALING = 'TRIALING',
  PAST_DUE = 'PAST_DUE',
  STARTER = 'STARTER',
  APP_TRIAL = 'APP_TRIAL',
  APP_TRIAL_EXPIRED = 'APP_TRIAL_EXPIRED',
}

export const SUBSCRIBED_UI_STATE_KINDS: ReadonlySet<BillingUiStateKind> =
  new Set([
    BillingUiStateKind.ACTIVE,
    BillingUiStateKind.TRIALING,
    BillingUiStateKind.PAST_DUE,
  ]);

export interface BillingUiState {
  kind: BillingUiStateKind;
  cancelAtPeriodEnd: boolean;
}

export enum BillingInterval {
  MONTH = 'month',
  YEAR = 'year',
}

// Stripe Price `currency_options` keys this codebase is prepared to render and
// charge in — usd is every Price's base currency, gbp/brl are `currency_options`
// entries. Adding a currency here does nothing on its own: it also needs a
// `currency_options` entry on both live Prices in Stripe.
export enum BillingCurrency {
  USD = 'usd',
  GBP = 'gbp',
  BRL = 'brl',
}

export const DEFAULT_BILLING_CURRENCY = BillingCurrency.USD;

export const SUPPORTED_BILLING_CURRENCIES: ReadonlySet<BillingCurrency> =
  new Set(Object.values(BillingCurrency));

export function isBillingCurrency(value: unknown): value is BillingCurrency {
  return (
    typeof value === 'string' &&
    SUPPORTED_BILLING_CURRENCIES.has(value as BillingCurrency)
  );
}

export interface TeamBillingPriceCurrencyOption {
  currency: BillingCurrency;
  unitAmount: number;
  taxBehavior?: string;
}

export enum TeamPlan {
  STARTER = 'starter',
  TEAM = 'team',
  ENTERPRISE = 'enterprise',
}

export const STRIPE_TEAM_MONTHLY_LOOKUP_KEY = 'price_team_monthly';
export const STRIPE_TEAM_ANNUAL_LOOKUP_KEY = 'price_team_annual';

export const STRIPE_TEAM_PRICE_LOOKUP_KEYS: readonly string[] = [
  STRIPE_TEAM_MONTHLY_LOOKUP_KEY,
  STRIPE_TEAM_ANNUAL_LOOKUP_KEY,
];

// Cached on the OWNER row of HTTeams (PK id=teamId, SK userId). Written
// exclusively by Stripe webhook handlers; never by a GraphQL mutation.
// Timestamps are epoch millis, matching the rest of the codebase.
export interface TeamBillingCache {
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeSubscriptionItemId?: string;
  subscriptionStatus?: StripeSubscriptionStatus;
  planInterval?: BillingInterval;
  priceLookupKey?: string;
  priceUnitAmount?: number;
  priceCurrency?: string;
  seatQuantity?: number;
  currentPeriodStart?: number;
  currentPeriodEnd?: number;
  stripeTrialEnd?: number;
  cancelAtPeriodEnd?: boolean;
  paymentMethodBrand?: string;
  paymentMethodLast4?: string;
  paymentMethodExpMonth?: number;
  paymentMethodExpYear?: number;
  lastInvoiceStatus?: string;
  pastDueReason?: string;
  nextPaymentAttempt?: number;
  billingEmail?: string;
}

export const TEAM_BILLING_CACHE_FIELDS: readonly (keyof TeamBillingCache)[] = [
  'stripeCustomerId',
  'stripeSubscriptionId',
  'stripeSubscriptionItemId',
  'subscriptionStatus',
  'planInterval',
  'priceLookupKey',
  'priceUnitAmount',
  'priceCurrency',
  'seatQuantity',
  'currentPeriodStart',
  'currentPeriodEnd',
  'stripeTrialEnd',
  'cancelAtPeriodEnd',
  'paymentMethodBrand',
  'paymentMethodLast4',
  'paymentMethodExpMonth',
  'paymentMethodExpYear',
  'lastInvoiceStatus',
  'pastDueReason',
  'nextPaymentAttempt',
  'billingEmail',
];

// Everything the clear-cache path removes on cancellation — every cache field
// except stripeCustomerId, which is preserved so a returning customer reuses
// the same Stripe Customer instead of a new one being created.
export const TEAM_BILLING_SUBSCRIPTION_FIELDS: readonly (keyof TeamBillingCache)[] =
  TEAM_BILLING_CACHE_FIELDS.filter((field) => field !== 'stripeCustomerId');
